import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';

import type { MoiFormikValues, MoiStepProps } from '~/components/exposures/moi/MoiStepper/types';
import useOrganization from '~/components/OrganizationContext';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';
import { getIsXactContractor } from '~/Utils';

const NO_RELEVANT_MOI_EXPERTISE_MESSAGE = 'Selected contact does not have relevant MOI expertises';

const hasRelevantMoiExpertise = (contact: ContactFullModel, moiMethodExpertises: string[]): boolean =>
  !isEmpty(moiMethodExpertises.filter((expertise) => contact?.moi_expertises?.includes(expertise)));

type UseSelectVendorContactParams = Pick<
  MoiStepProps,
  'getIsContactVendorApi' | 'setIsVendorApi' | 'setIsXactContractor' | 'setSelectedContact' | 'moiMethodsReturn'
>;

const useSelectVendorContact = ({
  getIsContactVendorApi,
  setIsVendorApi,
  setIsXactContractor,
  setSelectedContact,
  moiMethodsReturn,
}: UseSelectVendorContactParams): ((contact: ContactFullModel, isCancelled?: () => boolean) => Promise<void>) => {
  const { values, setFieldValue, setFieldTouched, setFieldError } = useFormikContext<MoiFormikValues>();
  const { organizationId }: { organizationId?: number } = useOrganization();
  const { getMoiMethodExpertises } = moiMethodsReturn;

  // A pick the caller no longer wants by the time both lookups return (isCancelled) writes nothing
  return async (contact: ContactFullModel, isCancelled: () => boolean = () => false) => {
    if (!hasRelevantMoiExpertise(contact, getMoiMethodExpertises(values['moi_method_id']))) {
      setFieldTouched('contact_id', true);
      setFieldError('contact_id', NO_RELEVANT_MOI_EXPERTISE_MESSAGE);
      return;
    }

    const { data } = await getIsContactVendorApi(contact);
    const ans = await getIsXactContractor(contact, organizationId);
    if (isCancelled()) {
      return;
    }

    setIsVendorApi(data?.is_vendor_api);
    if (!data?.is_vendor_api && ['completed', 'accepted'].includes(values['status'])) {
      setFieldValue('status', null);
      setFieldTouched('status', false);
    }
    setIsXactContractor(ans?.is_xactanalysis_contractor);

    setSelectedContact(contact);
    setFieldValue('contact_id', contact.id);
    // setFieldValue already validated the new contact_id; a validating touch would check the last render's
    // values, where contact_id is not set yet, and could settle last with a stale Required
    setFieldTouched('contact_id', true, false);
  };
};

export default useSelectVendorContact;
